import { CircularProgress, Input } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import React, { useState } from 'react';

import { ReactComponent as GetPluggedIntoPlexSvg } from '../../../assets/get_plugged_into_plex_art.svg';
import { ReactComponent as SearchIconSvg } from '../../../assets/search.svg';
import { OnboardingStep, useDomainPlexesQuery, useUpdateUserSettingsMutation } from '../../../graphql/gen/types';
import { DOMAIN_PLEXES_INITIAL_QUERY_VARIABLES } from '../../../graphql/queries/DomainPlexes';
import { colorPalette, spacings, TextSizesEnum, topBorderStyle } from '../../../theme';
import { ButtonColor, PlexButton } from '../../shared/primitives/PlexButton';
import { PlexTypography } from '../../shared/primitives/PlexTypography';
import { PlexView } from '../dialog/browse_plexes/PlexView';
import { OnboardingStepSelector } from './OnboardingStepSelector';

interface Props {
  changeOnboardingStep: (step: OnboardingStep) => void;
  completedOnboardingSteps: OnboardingStep[];
  currentStep: OnboardingStep;
  onNext: () => void;
}

export const GetPluggedIntoPlex = ({
  changeOnboardingStep,
  completedOnboardingSteps,
  currentStep,
  onNext,
}: Props): React.ReactElement => {
  const classes = useStyles();
  const [updateUserSettings, { loading }] = useUpdateUserSettingsMutation();
  const [searchQuery, setSearchQuery] = useState('');
  const { data: currentUser, loading: loadingCurrentUser } = useDomainPlexesQuery({
    variables: DOMAIN_PLEXES_INITIAL_QUERY_VARIABLES,
  });

  if (loadingCurrentUser) {
    return <CircularProgress size={24} />;
  }
  const plexes =
    currentUser?.currentUser?.domain?.plexes?.items.filter((p) => {
      if (p.deleted) {
        return false;
      }
      if (!searchQuery) {
        return true;
      }
      if (p.name.toLowerCase().indexOf(searchQuery.toLowerCase()) !== -1) {
        return true;
      }
      if (p.description?.toLowerCase().indexOf(searchQuery.toLowerCase()) !== -1) {
        return true;
      }
      return false;
    }) || [];

  return (
    <div className={classes.root}>
      <div className={classes.rootContent}>
        <div className={classes.leftContainer}>
          <PlexTypography className={classes.title} text={'Get Plugged Into Plex'} size={TextSizesEnum.XXXLarge} />
          <PlexTypography
            className={classes.message}
            size={TextSizesEnum.NormalPlus}
            text={
              'Plex keeps you in-the-loop on important updates and\nknowledge for teams, projects or people at your company.\nLet’s find some Plexes for you to follow.'
            }
          />
          <OnboardingStepSelector
            changeOnboardingStep={changeOnboardingStep}
            completedOnboardingSteps={completedOnboardingSteps}
            currentStep={currentStep}
          />
          <div className={classes.artLeft}>
            <GetPluggedIntoPlexSvg />
          </div>
        </div>
        <div className={classes.rightContainer}>
          <div className={classes.rightContainerInner}>
            <div className={classes.rightContainerContent}>
              <div className={classes.searchContainer}>
                <div className={classes.searchRoot}>
                  <SearchIconSvg className={classes.searchIcon} />
                  <Input
                    className={classes.searchInput}
                    placeholder={'Search Plex by name, keyword, channel or people'}
                    fullWidth={true}
                    disableUnderline={true}
                    onChange={(event: React.ChangeEvent<{ name?: string; value: unknown }>): void => {
                      setSearchQuery(event.target.value as string);
                    }}
                  />
                </div>
              </div>
              <div className={classes.plexesList}>
                {plexes.map((p) => (
                  <PlexView key={p.id} plex={p} />
                ))}
              </div>
            </div>
            <div className={classes.actionButtonsContainer}>
              {loading && <CircularProgress size={24} />}
              <PlexButton
                className={classes.skipButton}
                label={'Save & Finish Later'}
                onClick={onNext}
                color={ButtonColor.TransparentBlack}
              />
              <PlexButton
                className={classes.nextButton}
                label={'Next'}
                onClick={(): void => {
                  updateUserSettings({
                    variables: {
                      userSettingsInput: {
                        completedOnboardingStep: OnboardingStep.GetPluggedIntoPlex,
                      },
                    },
                    update: (_, { errors }) => {
                      if (!errors?.length) {
                        onNext();
                      } else {
                        console.error('got err when next on get started:', errors);
                      }
                    },
                  });
                }}
                color={ButtonColor.Primary}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      overflowY: 'hidden',
      overflowX: 'auto',
      height: '100vh',
      width: '100%',
      paddingTop: 80,
    },
    rootContent: {
      display: 'flex',
      flexDirection: 'row',
      width: '100%',
      height: '100%',
    },
    leftContainer: {
      display: 'flex',
      flexDirection: 'column',
      paddingRight: 56,
      width: 570,
      backgroundColor: colorPalette.purpleGray._50,
    },
    artLeft: {
      display: 'flex',
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      marginTop: spacings.xlarge,
      marginBottom: spacings.xlarge,
    },
    rightContainer: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      justifyContent: 'center',
      backgroundColor: '#fff',
      boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1), 0px 0px 2px rgba(0, 0, 0, 0.1)',
    },
    rightContainerInner: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      overflowY: 'hidden',
    },
    rightContainerContent: {
      display: 'flex',
      flexDirection: 'column',
      overflowY: 'auto',
    },
    title: {
      color: '#464663',
      fontWeight: 'bold',
      marginTop: 68,
      marginLeft: 56,
    },
    message: {
      color: colorPalette.purpleGray._400,
      marginTop: spacings.xlarge,
      marginBottom: spacings.xlarge * 2,
      marginLeft: 56,
    },
    actionButtonsContainer: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'flex-end',
      alignItems: 'center',
      paddingTop: spacings.xlarge,
      paddingBottom: spacings.xlarge,
      paddingRight: spacings.xlarge,
      ...topBorderStyle,
    },
    skipButton: {
      height: 52,
      borderRadius: 36,
    },
    nextButton: {
      height: 52,
      width: 112,
      borderRadius: 36,
    },
    searchContainer: {
      display: 'flex',
      alignItems: 'center',
      width: '100%',
      backgroundColor: colorPalette.purpleGray._50,
    },
    searchRoot: {
      display: 'flex',
      alignItems: 'center',
      flexDirection: 'row',
      position: 'relative',
      margin: spacings.xlarge,
      width: '100%',
      backgroundColor: '#fff',
      borderRadius: 40,
      boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1), 0px 0px 2px rgba(0, 0, 0, 0.1)',
    },
    searchIcon: {
      position: 'absolute',
      marginLeft: spacings.normal,
    },
    searchInput: {
      display: 'flex',
      height: 60,
      paddingLeft: 56,
    },
    plexesList: {
      display: 'flex',
      flexWrap: 'wrap',
      margin: 36,
    },
  })
);
